import React, { useEffect, useState } from "react";
import { Briefcase, MapPin, Clock, DollarSign, CheckCircle2, Loader2, X, PartyPopper } from "lucide-react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const inp = "w-full bg-[#101311] rounded-lg px-3 py-2.5 text-sm border border-emerald-800/50 outline-none focus:border-amber-400 text-[#FDFBF7]";
const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const PERIODS = ["Morning", "Afternoon", "Evening", "Flexible"];

export default function CareersPage() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [applyJob, setApplyJob] = useState(null);

  useEffect(() => {
    api.get("/careers/jobs").then((r) => setJobs(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen">
      <section className="relative py-20 tropical-grain border-b border-emerald-900/50">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 text-center">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-bold text-amber-400 bg-emerald-950/60 border border-amber-500/30 rounded-full px-4 py-2">
            <Briefcase className="w-3.5 h-3.5" /> We're Hiring
          </span>
          <h1 className="font-display font-black uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl mt-6">
            Join the <span className="text-gradient-gold">Sir. King Kitchen</span> Team
          </h1>
          <p className="text-lg text-[#FDFBF7]/80 mt-4">Good Food. Great Service. Great People.</p>
          <p className="text-sm text-[#FDFBF7]/55 mt-2 max-w-xl mx-auto">Be part of a growing local restaurant bringing bold Caribbean flavor to Maine. We'd love to meet you.</p>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-5 sm:px-8 py-14">
        {loading ? (
          <div className="grid place-items-center py-16 text-amber-400"><Loader2 className="w-8 h-8 animate-spin" /></div>
        ) : jobs.length === 0 ? (
          <div className="text-center rounded-3xl border border-dashed border-emerald-800/60 py-16">
            <Briefcase className="w-10 h-10 text-amber-400/50 mx-auto" />
            <p className="text-[#FDFBF7]/70 mt-4 font-display font-bold uppercase">No open positions right now</p>
            <p className="text-[#FDFBF7]/50 text-sm mt-1">Check back soon — we're always growing.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-5">
            {jobs.map((j) => (
              <JobCard key={j.job_id} job={j} onApply={() => setApplyJob(j)} />
            ))}
          </div>
        )}
      </section>

      <ApplyDialog job={applyJob} onClose={() => setApplyJob(null)} />
    </div>
  );
}

function JobCard({ job, onApply }) {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid={`job-card-${job.job_id}`} className="rounded-3xl bg-[#181D1A] border border-emerald-900/60 p-6 flex flex-col hover:border-amber-500/50 transition-colors">
      <h2 className="font-display font-black uppercase text-2xl">{job.title}</h2>
      <div className="flex flex-wrap gap-2 mt-3">
        {(job.highlights || []).map((h) => (
          <span key={h} className="px-3 py-1 rounded-full bg-emerald-900/60 border border-amber-500/30 text-amber-300 text-xs font-bold">{h}</span>
        ))}
      </div>
      <div className="text-sm text-[#FDFBF7]/60 mt-4 space-y-1.5">
        <div className="flex items-center gap-2"><Briefcase className="w-4 h-4 text-amber-400" /> {job.employment_type}</div>
        <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-amber-400" /> {job.location}</div>
        {job.compensation && <div className="flex items-start gap-2"><DollarSign className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" /> <span className="text-amber-300 font-semibold">{job.compensation}</span></div>}
      </div>

      {open && (
        <div className="mt-4 text-sm text-[#FDFBF7]/75 space-y-4 animate-fade-up">
          {job.description && <p className="leading-relaxed">{job.description}</p>}
          {job.requirements?.length > 0 && (
            <div>
              <div className="font-display font-bold uppercase text-amber-400 text-xs tracking-wide mb-2">What We're Looking For</div>
              <ul className="space-y-1.5">{job.requirements.map((r) => <li key={r} className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" /> {r}</li>)}</ul>
            </div>
          )}
          {job.responsibilities?.length > 0 && (
            <div>
              <div className="font-display font-bold uppercase text-amber-400 text-xs tracking-wide mb-2">Responsibilities</div>
              <ul className="space-y-1.5">{job.responsibilities.map((r) => <li key={r} className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" /> {r}</li>)}</ul>
            </div>
          )}
          {job.schedule && <div className="flex items-center gap-2 text-[#FDFBF7]/60"><Clock className="w-4 h-4 text-amber-400" /> {job.schedule}</div>}
          {job.deadline && <p className="text-xs text-[#FDFBF7]/50">Apply by: {job.deadline}</p>}
        </div>
      )}

      <div className="flex gap-2 mt-auto pt-5">
        <button data-testid={`view-position-${job.job_id}`} onClick={() => setOpen((o) => !o)} className="flex-1 py-3 rounded-full border border-emerald-700/50 text-amber-300 font-display font-bold uppercase text-sm">{open ? "Hide" : "View Position"}</button>
        {job.active ? (
          <button data-testid={`apply-now-${job.job_id}`} onClick={onApply} className="flex-1 py-3 rounded-full bg-amber-400 hover:bg-amber-300 text-[#0A2818] font-display font-black uppercase text-sm active:scale-95 transition-transform">Apply Now</button>
        ) : (
          <span className="flex-1 py-3 rounded-full bg-emerald-950/60 border border-emerald-800/50 text-[#FDFBF7]/50 font-display font-bold uppercase text-xs grid place-items-center">Position Currently Closed</span>
        )}
      </div>
    </div>
  );
}

const emptyForm = {
  first_name: "", last_name: "", phone: "", email: "", city: "",
  has_license: null, has_transport: null, driving_record: "",
  hours_per_week: "", prior_experience: null, experience_text: "", additional_comments: "", consent: false,
  resume_path: null, resume_name: null,
};

function ApplyDialog({ job, onClose }) {
  const [f, setF] = useState(emptyForm);
  const [availability, setAvailability] = useState({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [uploading, setUploading] = useState(false);

  const uploadResume = async (file) => {
    if (!file) return;
    const okTypes = ["application/pdf", "application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"];
    if (!okTypes.includes(file.type)) { toast.error("Please upload a PDF, DOC or DOCX file."); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Resume is too large. Please keep it under 5MB."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/careers/upload-resume", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setF((p) => ({ ...p, resume_path: r.data.resume_path, resume_name: r.data.resume_name }));
      toast.success("Resume attached");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not upload your resume. You can still submit without it.");
    } finally { setUploading(false); }
  };

  useEffect(() => { if (job) { setF(emptyForm); setAvailability({}); setDone(false); } }, [job]);
  if (!job) return null;

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const set2Resume = (path, name) => setF((p) => ({ ...p, resume_path: path, resume_name: name }));
  const toggleDay = (day) => setAvailability((p) => { const n = { ...p }; if (n[day]) delete n[day]; else n[day] = []; return n; });
  const togglePeriod = (day, per) => setAvailability((p) => {
    const cur = p[day] || [];
    return { ...p, [day]: cur.includes(per) ? cur.filter((x) => x !== per) : [...cur, per] };
  });

  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((f.email || "").trim());
  const phoneOk = ((f.phone || "").replace(/[^0-9]/g, "").length >= 10);
  const valid = f.first_name.trim() && f.last_name.trim() && phoneOk && emailOk && f.consent;

  const submit = async () => {
    if (busy) return;
    if (!f.first_name.trim() || !f.last_name.trim()) { toast.error("Please enter your first and last name."); return; }
    if (!emailOk) { toast.error("Please enter a valid email address."); return; }
    if (!phoneOk) { toast.error("Please enter a valid phone number (at least 10 digits)."); return; }
    if (!f.consent) { toast.error("Please confirm the certification checkbox to submit."); return; }
    setBusy(true);
    try {
      const res = await api.post("/careers/apply", { job_id: job.job_id, ...f, availability });
      if (res?.data?.ok) {
        setDone(true);
      } else {
        toast.error("We couldn't submit your application right now. Your information has not been lost. Please try again or contact us directly.");
      }
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(typeof msg === "string" && msg ? msg : "We couldn't submit your application right now. Your information has not been lost. Please try again or contact us directly.");
    } finally { setBusy(false); }
  };

  const YesNo = ({ label, field, options = ["Yes", "No"] }) => (
    <div>
      <div className="text-sm text-[#FDFBF7]/80 mb-1.5">{label}</div>
      <div className="flex flex-wrap gap-2">
        {options.map((o) => {
          const val = o === "Yes" ? true : o === "No" ? false : o;
          const active = f[field] === val;
          return <button key={o} type="button" data-testid={`apply-${field}-${o.toLowerCase().replace(/[^a-z]/g, "-")}`} onClick={() => set(field, val)} className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${active ? "bg-amber-400 text-[#0A2818] border-amber-400" : "border-emerald-700/50 text-[#FDFBF7]/80"}`}>{o}</button>;
        })}
      </div>
    </div>
  );

  return (
    <Dialog open={!!job} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#181D1A] border-emerald-800 text-[#FDFBF7] max-w-lg max-h-[90vh] overflow-y-auto p-0">
        <DialogTitle className="sr-only">Apply for {job.title}</DialogTitle>
        {done ? (
          <div className="p-8 text-center" data-testid="apply-thank-you">
            <span className="grid place-items-center w-16 h-16 rounded-full bg-amber-400 text-[#0A2818] mx-auto"><PartyPopper className="w-8 h-8" /></span>
            <h2 className="font-display font-black text-2xl uppercase mt-4">Thank You for Applying!</h2>
            <p className="text-[#FDFBF7]/70 text-sm mt-3">Thank you for applying to Sir. King Kitchen! Your application has been received. Our team will review your application and contact you if we would like to move forward.</p>
            <button onClick={onClose} className="mt-6 px-6 py-3 rounded-full bg-amber-400 text-[#0A2818] font-display font-black uppercase text-sm">Done</button>
          </div>
        ) : (
          <div className="p-6">
            <div className="flex justify-between items-start">
              <div>
                <h2 className="font-display font-black text-2xl uppercase">Apply — {job.title}</h2>
                <p className="text-xs text-[#FDFBF7]/50 mt-1">Your info is used only for employment consideration and is kept private.</p>
              </div>
              <button onClick={onClose} className="text-[#FDFBF7]/50 hover:text-amber-300"><X className="w-5 h-5" /></button>
            </div>

            <div className="mt-5 space-y-5">
              <div>
                <div className="font-display font-bold uppercase text-amber-400 text-xs tracking-wide mb-2">Personal Information</div>
                <div className="grid grid-cols-2 gap-2">
                  <input data-testid="apply-first-name" value={f.first_name} onChange={(e) => set("first_name", e.target.value)} placeholder="First name*" className={inp} />
                  <input data-testid="apply-last-name" value={f.last_name} onChange={(e) => set("last_name", e.target.value)} placeholder="Last name*" className={inp} />
                  <input data-testid="apply-phone" type="tel" autoComplete="tel" value={f.phone} onChange={(e) => set("phone", e.target.value)} placeholder="Phone*" className={inp} />
                  <input data-testid="apply-email" type="email" autoComplete="email" value={f.email} onChange={(e) => set("email", e.target.value)} placeholder="Email*" className={inp} />
                  <input data-testid="apply-city" value={f.city} onChange={(e) => set("city", e.target.value)} placeholder="City/Town" className={`${inp} col-span-2`} />
                </div>
              </div>

              <div className="space-y-3">
                <div className="font-display font-bold uppercase text-amber-400 text-xs tracking-wide">Driver Information</div>
                <YesNo label="Do you have a valid driver's license?" field="has_license" />
                <YesNo label="Do you have reliable transportation?" field="has_transport" />
                <YesNo label="Do you have a clean/reliable driving record?" field="driving_record" options={["Yes", "No", "Prefer to discuss"]} />
              </div>

              <div>
                <div className="font-display font-bold uppercase text-amber-400 text-xs tracking-wide mb-2">Availability</div>
                <div className="space-y-2">
                  {DAYS.map((day) => {
                    const on = day in availability;
                    return (
                      <div key={day} className="rounded-lg border border-emerald-800/40 p-2">
                        <button type="button" data-testid={`apply-day-${day.toLowerCase()}`} onClick={() => toggleDay(day)} className={`text-sm font-semibold ${on ? "text-amber-400" : "text-[#FDFBF7]/70"}`}>
                          <span className={`inline-block w-4 h-4 mr-2 rounded border ${on ? "bg-amber-400 border-amber-400" : "border-emerald-700"}`} />{day}
                        </button>
                        {on && (
                          <div className="flex flex-wrap gap-1.5 mt-2 pl-6">
                            {PERIODS.map((per) => {
                              const active = (availability[day] || []).includes(per);
                              return <button key={per} type="button" onClick={() => togglePeriod(day, per)} className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${active ? "bg-amber-400 text-[#0A2818] border-amber-400" : "border-emerald-700/50 text-[#FDFBF7]/70"}`}>{per}</button>;
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                <input data-testid="apply-hours" value={f.hours_per_week} onChange={(e) => set("hours_per_week", e.target.value)} placeholder="How many hours per week are you looking to work?" className={`${inp} mt-2`} />
              </div>

              <div className="space-y-3">
                <div className="font-display font-bold uppercase text-amber-400 text-xs tracking-wide">Experience</div>
                <YesNo label="Have you worked as a delivery driver before?" field="prior_experience" />
                {f.prior_experience === true && (
                  <textarea data-testid="apply-experience" value={f.experience_text} onChange={(e) => set("experience_text", e.target.value)} rows={2} placeholder="Tell us briefly about your delivery experience." className={inp} />
                )}
                <textarea data-testid="apply-comments" value={f.additional_comments} onChange={(e) => set("additional_comments", e.target.value)} rows={2} placeholder="Tell us anything else you'd like us to know about you. (optional)" className={inp} />
              </div>

              <div className="space-y-2">
                <div className="font-display font-bold uppercase text-amber-400 text-xs tracking-wide">Resume (optional)</div>
                {f.resume_name ? (
                  <div className="flex items-center justify-between gap-2 rounded-lg border border-emerald-700/50 bg-emerald-950/30 px-3 py-2.5" data-testid="apply-resume-attached">
                    <span className="text-sm truncate">📎 {f.resume_name}</span>
                    <button type="button" data-testid="apply-resume-remove" onClick={() => set2Resume(null, null)} className="text-red-400 text-xs font-bold shrink-0">Remove</button>
                  </div>
                ) : (
                  <label data-testid="apply-resume-label" className="flex items-center justify-center gap-2 rounded-lg border border-dashed border-emerald-700/60 px-3 py-3 text-sm text-[#FDFBF7]/70 cursor-pointer hover:border-amber-400">
                    {uploading ? "Uploading…" : "Attach resume (PDF, DOC, DOCX · max 5MB)"}
                    <input data-testid="apply-resume-input" type="file" accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document" className="hidden" disabled={uploading} onChange={(e) => uploadResume(e.target.files?.[0])} />
                  </label>
                )}
              </div>

              <label className="flex items-start gap-2 text-sm text-[#FDFBF7]/75">
                <input data-testid="apply-consent" type="checkbox" checked={f.consent} onChange={(e) => set("consent", e.target.checked)} className="mt-1" />
                I certify that the information I have provided is accurate and understand that submitting this application does not guarantee employment.
              </label>

              <button data-testid="apply-submit" onClick={submit} disabled={busy || !valid} className="w-full py-3.5 rounded-full bg-amber-400 hover:bg-amber-300 disabled:opacity-40 disabled:cursor-not-allowed text-[#0A2818] font-display font-black uppercase tracking-wide flex items-center justify-center gap-2">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Submit Application
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
